// External dependencies
import { ErrorMessage } from '@hookform/error-message';
import cn from 'classnames';
import { type FC } from 'react';
import { FormProvider, useForm, useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';

import { _isNotEmpty, _notNil } from '@/littledash';

// Model types
import type { MetadataColumns } from '@/model/Metadata.model';

// Support modules
import { isValidLinkAttribute } from '@/helpers';

// Internal components
import Button from '@/components/UI/Button';
import { Text } from '@/components/UI/FormFields/Latest';

// Relative imports
import { getMetadataFields } from './AddEditLinks.utils';

// Styles
import styles from './ModalFieldErrors.module.scss';

interface FormValues {
  name: string;
  url: string;
  alt: string;
  metadata?: Record<string, unknown>;
}
interface AddEditLinkProps {
  handleSave: (data: FormValues) => void;
  link: { name: string; url: string; alt: string };
  metadata: Array<MetadataColumns>;
}

interface AddEditLinkFormProps extends AddEditLinkProps {
  isEdit: boolean;
}

/**
 * Modal wrapper that provides the react-hook-form context for adding or editing a link attachment,
 * including any required or optional attachment metadata fields.
 */
const AddEditLink: FC<AddEditLinkProps> = ({ handleSave, link, metadata }) => {
  const isEdit = Boolean(link);
  const formMethods = useForm();

  return (
    <>
      <FormProvider {...formMethods}>
        <AddEditLinkForm handleSave={handleSave} isEdit={isEdit} link={link} metadata={metadata} />
      </FormProvider>
    </>
  );
};

/**
 * Link form body. Link name, URL and required metadata are validated on submit, and missing required
 * fields show a "This field is required" message under the field.
 */
const AddEditLinkForm: FC<AddEditLinkFormProps> = ({ handleSave, isEdit, link, metadata }) => {
  const {
    handleSubmit,
    formState: { errors },
  } = useFormContext<FormValues>();

  const formContext = useFormContext<FormValues>();
  const register = formContext ? formContext.register : () => ({});
  const dispatch = useDispatch();

  const closeModal = () => {
    dispatch({
      type: 'CLOSE_MODAL',
    });
  };

  return (
    <form onSubmit={handleSubmit(handleSave)} className="center mw6 ui-card">
      <div className="pa4">
        <h3 className="normal f4">{isEdit ? 'Edit link' : 'Add a link'}</h3>
      </div>
      <div className={cn('overflow-visible', styles.fields)}>
        <div className="pb4 ph4">
          <Text testId="link-name-text-field" value={link?.name} name="name" label="Link name" required />
        </div>
        <div className="pb4 ph4">
          <label htmlFor="url">URL (required)</label>
          <input
            type="text"
            data-testid="url-input"
            data-test-component="Text"
            data-test-element="input"
            style={{ marginBottom: 0 }}
            className={_notNil(errors.url) ? 'input__error' : ''}
            defaultValue={link?.url}
            {...register('url', {
              required: 'This field is required',
              validate: (value) => isValidLinkAttribute(value) || 'Invalid URL',
              maxLength: { value: 1000, message: 'Maximum of 1000' },
            })}
          />
          <ErrorMessage
            errors={errors}
            name="url"
            render={({ message }) => (
              <p data-testid="url-error-message" className="f6 red db pt2">
                {message}
              </p>
            )}
          />
        </div>

        <div className="pb4 ph4">
          <Text value={link?.alt} testId="alt-text-field" name="alt" label="Description" maxLength={1000} />
        </div>
        {_isNotEmpty(metadata) && <div className="pb4 ph4">{getMetadataFields(metadata, link)}</div>}
      </div>
      <div className="bt b--moon-gray bg-white pa3">
        <Button submit className="mr2" testId="save-link">
          Save
        </Button>
        <Button plain onClick={closeModal} testId="cancel-link">
          Cancel
        </Button>
      </div>
    </form>
  );
};

export default AddEditLink;
